import { useEffect, useRef, useState } from 'react'
import { Upload, Square } from 'lucide-react'
import { pollApi, presentationApi } from '../../services/api'
import { useLiveEventStore } from '../../stores/liveEventStore'

// Host-only launcher shown above the control bar: pick a pre-authored poll
// set or presentation to run, or upload a new PDF on the spot.
export default function LaunchEventMenu({ channelId, onClose }: { channelId: string; onClose: () => void }) {
  const [sets, setSets] = useState<any[]>([])
  const [presentations, setPresentations] = useState<any[]>([])
  const [busy, setBusy] = useState(false)
  const fileRef = useRef<HTMLInputElement>(null)
  // OSPROD-272: Stop used to exist ONLY inside the presentation overlay, so a
  // host who closed the overlay locally (or whose document was deleted out
  // from under the run) had no way to end it for the room. This menu is
  // already host-gated by the caller, so it's the natural home for Stop.
  const activeRun = useLiveEventStore((s) => s.presentation)

  const load = () => {
    pollApi.list(channelId).then((r) => setSets(r.data.sets)).catch(() => {})
    presentationApi.list(channelId).then((r) => setPresentations(r.data.presentations)).catch(() => {})
  }
  useEffect(load, [channelId])

  const runPoll = async (setId: string) => {
    try { await pollApi.run(channelId, setId); onClose() } catch { /* overlay surfaces state */ }
  }
  const runPresentation = async (pid: string) => {
    try { await presentationApi.run(channelId, pid); onClose() } catch { /* overlay surfaces state */ }
  }
  const stopPresentation = async () => {
    if (!activeRun) return
    try { await presentationApi.patchRun(channelId, activeRun.run.id, { state: 'CLOSED' }); onClose() }
    catch { /* keep menu open */ }
  }
  const onFile = async (f: File) => {
    const form = new FormData()
    form.append('file', f)
    form.append('title', f.name.replace(/\.pdf$/i, ''))
    setBusy(true)
    try { await presentationApi.upload(channelId, form); load() }
    catch { /* keep menu open */ }
    finally { setBusy(false) }
  }

  return (
    <div className="absolute bottom-16 left-1/2 -translate-x-1/2 z-40 w-80 bg-card border border-border rounded-xl shadow-lg p-3 space-y-3">
      <div>
        <div className="text-xs text-muted-foreground uppercase font-semibold mb-2">Start poll</div>
        {sets.length === 0 && (
          <div className="text-sm text-muted-foreground">No polls yet. Create them in channel settings.</div>
        )}
        <div className="space-y-1 max-h-48 overflow-auto">
          {sets.map((s) => (
            <button
              key={s.id}
              onClick={() => runPoll(s.id)}
              className="w-full text-left px-3 py-2 text-sm rounded hover:bg-muted text-foreground"
            >
              {s.title}{' '}
              <span className="text-muted-foreground">
                · {s.questions.length} q.{s.isAnonymous ? ' · anon' : ''}
              </span>
            </button>
          ))}
        </div>
      </div>

      {activeRun && (
        <button
          onClick={stopPresentation}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm rounded bg-destructive/10 text-destructive hover:bg-destructive/20 transition"
        >
          <Square size={13} /> Stop presentation for everyone
        </button>
      )}

      <div className="border-t border-border pt-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs text-muted-foreground uppercase font-semibold">Present</div>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={busy}
            className="text-xs text-primary flex items-center gap-1"
          >
            <Upload size={13} /> {busy ? 'Uploading…' : 'PDF'}
          </button>
          <input
            ref={fileRef}
            type="file"
            accept="application/pdf"
            hidden
            onChange={(e) => e.target.files?.[0] && onFile(e.target.files[0])}
          />
        </div>
        <div className="space-y-1 max-h-48 overflow-auto">
          {presentations.map((p) => (
            <button
              key={p.id}
              onClick={() => runPresentation(p.id)}
              className="w-full text-left px-3 py-2 text-sm rounded hover:bg-muted text-foreground"
            >
              {p.title} <span className="text-muted-foreground">· {p.slideCount} sl.</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  )
}
